import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { FICHA_OBRA_PRINT_STYLES } from './ficha-obra-impressao.styles';

interface FichaItem {
  label: string;
  value: string;
}

interface FichaUnit {
  name: string;
  items: FichaItem[];
}

interface FichaTagGroup {
  title: string;
  tags: string[];
}

interface FichaTimelineItem {
  date: string | null;
  title: string | null;
  text: string | null;
}

interface FichaContact {
  name: string;
  role: string | null;
  lines: string[];
}

interface FichaCompany {
  name: string;
  meta: string | null;
  phones: string[];
  contacts: FichaContact[];
}

interface FichaTagSection {
  title: string;
  groups: FichaTagGroup[];
}

interface FichaTextSection {
  title: string;
  rows: FichaItem[];
}

interface FichaTimelineSection {
  title: string;
  items: FichaTimelineItem[];
}

export interface FichaView {
  id: number;
  name: string;
  address: string;
  module: string | null;
  workType: string | null;
  phase: string | null;
  endingDate: string | null;
  updateDate: string | null;
  responsible: string | null;
  potential: number;
  numbersTitle: string;
  numbers: FichaItem[];
  units: FichaUnit[];
  companies: FichaCompany[];
  companiesSummary: string;
  tagSections: FichaTagSection[];
  textSections: FichaTextSection[];
  timelineSections: FichaTimelineSection[];
  updates: FichaTimelineItem[];
}

const REAL_ESTATE_MODULE_IDS = [2, 3, 4, 234];
const UNITS_CATEGORY_PREFIX = 'números - apartamentos';

const PARTNER_HIDDEN_KEYS = ['prazo', 'valordolar'];
const MORE_DETAILS_NAME = 'mais detalhes';
const AMOUNT_KEY = 'Valor';

const NUMBERS_ORDER = [
  'unidades', 'padrão', 'minha casa minha vida',
  'área útil', 'área total', 'área do terreno',
  'blocos', 'andares', 'unidades por andar',
  'vagas', 'banheiros', 'janelas',
  'elevadores', 'coberturas',
];

const LABELS: Record<string, string> = {
  valordolar: 'Valor (US$)',
  'unidades por andar': 'Unidades por andar',
  'área útil': 'Área útil',
  'área total': 'Área total',
  'área do terreno': 'Área do terreno',
};

@Component({
  selector: 'app-ficha-obra-impressao',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './ficha-obra-impressao.component.html',
  styleUrls: ['./ficha-obra-impressao.component.scss'],
  styles: [FICHA_OBRA_PRINT_STYLES],
  encapsulation: ViewEncapsulation.None,
})
export class FichaObraImpressaoComponent implements OnInit {
  @Input() idObra: number | number[] | null = null;
  @Input() obra: any;
  @Input() dadosApi: any;

  modalRef!: BsModalRef;
  isLoading: boolean = false;

  fichas: FichaView[] = [];

  readonly stars = [1, 2, 3, 4, 5];

  constructor(private readonly modalService: BsModalService) {}

  ngOnInit(): void {
    if (
      !this.dadosApi ||
      !Array.isArray(this.dadosApi.works) ||
      this.dadosApi.works.length === 0
    ) {
      console.error(
        'Os dados da API não foram fornecidos ou estão incompletos ao componente de impressão.'
      );
      this.obra = null;
      this.fichas = [];
      return;
    }

    this.obra = this.dadosApi.works[0];
    this.fichas = this.dadosApi.works
      .filter((work: any) => !!work?.workDetails)
      .map((work: any) => this.buildFicha(work));
  }

  abrirModalImpressao(): void {
    this.isLoading = true;
    this.modalRef = this.modalService.show(FichaObraImpressaoComponent, {
      initialState: {
        idObra: this.idObra,
        dadosApi: this.obra,
      },
      class: 'modal-lg',
    });
    this.isLoading = false;
  }

  imprimir(): void {
    const conteudo = document.getElementById('printOnly');
    if (!conteudo) return;

    const janela = window.open('', '_blank', 'width=900,height=700');
    if (!janela) return;

    janela.document.write(`
      <html lang="pt-br">
        <head>
          <meta charset="UTF-8">
          <title>Ficha da obra</title>
          <style>
            @page { size: A4; margin: 12mm; }
            html, body { margin: 0; padding: 0; background: #fff; }
            ${FICHA_OBRA_PRINT_STYLES}
          </style>
        </head>
        <body>
          ${conteudo.innerHTML}
          <script>
            window.onload = function () {
              window.focus();
              window.print();
            };
          </script>
        </body>
      </html>
    `);
    janela.document.close();
  }

  fechar(): void {
    if (this.modalRef) {
      this.modalRef.hide();
    } else {
      console.error('ModalRef não está definido');
    }
  }

  private buildFicha(work: any): FichaView {
    const details = work.workDetails;
    const categories: any[] = Array.isArray(details?.categories) ? details.categories : [];
    const moduleId = Number(details?.moduleId);
    const isRealEstate = REAL_ESTATE_MODULE_IDS.includes(moduleId);

    const numbersCategory = categories.find((c) => this.isDictionaryCategory(c)) ?? null;
    const unitsCategory = categories.find((c) => this.isUnitsCategory(c)) ?? null;
    const otherCategories = categories.filter(
      (c) => c !== numbersCategory && !this.isUnitsCategory(c)
    );

    const companies = this.buildCompanies(work.contacts?.companies ?? []);
    const totalContacts = companies.reduce((sum, c) => sum + c.contacts.length, 0);

    return {
      id: details.id,
      name: details.name ?? '',
      address: this.buildAddress(details.locationDescription) ?? 'Endereço não informado',
      module: this.clean(details.module),
      workType: this.clean(numbersCategory?.name),
      phase: this.clean(details.phase),
      endingDate: this.buildEndingDate(details.endingDate, numbersCategory, isRealEstate),
      updateDate: this.formatMonthYear(details.updateDate),
      responsible: this.clean(details.clientSpecific?.responsible),
      potential: Math.min(Math.max(Number(details.clientSpecific?.potentialScore) || 0, 0), 5),
      numbersTitle: isRealEstate ? 'Números da obra' : 'Detalhes da obra',
      numbers: isRealEstate
        ? this.sortNumbers(this.buildNumbers(numbersCategory))
        : this.buildPartnerNumbers(numbersCategory, otherCategories),
      units: this.buildUnits(unitsCategory),
      companies,
      companiesSummary: `${this.plural(companies.length, 'empresa', 'empresas')}, ${this.plural(totalContacts, 'contato', 'contatos')}`,
      tagSections: isRealEstate ? this.buildTagSections(otherCategories) : [],
      textSections: isRealEstate ? [] : this.buildTextSections(otherCategories),
      timelineSections: this.buildTimelineSections(otherCategories),
      updates: this.buildUpdates(details.recentUpdates),
    };
  }

  private buildEndingDate(endingDate: any, numbersCategory: any, isRealEstate: boolean): string | null {
    const formatted = this.formatMonthYear(endingDate);

    if (formatted) return formatted;
    if (isRealEstate) return null;

    return this.clean(numbersCategory?.values?.['Prazo']);
  }

  private formatMonthYear(value: any): string | null {
    if (!value) return null;

    const date = new Date(value);

    if (isNaN(date.getTime()) || date.getFullYear() <= 1900) return null;

    return new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' }).format(date);
  }

  private buildAddress(html: string | null | undefined): string | null {
    if (!html) return null;

    const text = html
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<[^>]+>/g, '');

    const decoder = document.createElement('textarea');
    decoder.innerHTML = text;

    const lines = decoder.value
      .split('\n')
      .map((line) => line.replace(/\s{2,}/g, ' ').trim())
      .filter((line) => line.length > 0);

    const zip = lines.filter((line) => line.toUpperCase().startsWith('CEP'));
    const parts = [...lines.filter((line) => !zip.includes(line)), ...zip];

    return parts.length ? parts.join(', ') : null;
  }

  private sortNumbers(items: FichaItem[]): FichaItem[] {
    const rank = (item: FichaItem) => {
      const idx = NUMBERS_ORDER.indexOf(item.label.trim().toLowerCase());
      return idx === -1 ? NUMBERS_ORDER.length : idx;
    };
    return items
      .map((item, index) => ({ item, index }))
      .sort((a, b) => rank(a.item) - rank(b.item) || a.index - b.index)
      .map(({ item }) => item);
  }

  private buildNumbers(category: any, hiddenKeys: string[] = []): FichaItem[] {
    if (!category?.values) return [];

    return Object.entries(category.values as Record<string, string>)
      .filter(
        ([key, value]) =>
          !this.isEmptyValue(value) && !hiddenKeys.includes(String(key).trim().toLowerCase())
      )
      .map(([key, value]) => ({
        label: this.getLabel(key),
        value: this.formatNumberValue(key, value),
      }));
  }

  private buildPartnerNumbers(numbersCategory: any, otherCategories: any[]): FichaItem[] {
    const items = this.buildNumbers(numbersCategory, PARTNER_HIDDEN_KEYS);

    const moreDetails = otherCategories.find(
      (c) =>
        this.isDictionaryCategory(c) &&
        String(c.name ?? '').trim().toLowerCase() === MORE_DETAILS_NAME
    );
    const amount = moreDetails?.values?.[AMOUNT_KEY];

    if (this.isEmptyValue(amount)) return items;

    const infoIndex = items.findIndex((item) => item.label.trim().toLowerCase() === 'info');
    items.splice(infoIndex < 0 ? items.length : infoIndex + 1, 0, {
      label: AMOUNT_KEY,
      value: String(amount).trim(),
    });

    return items;
  }

  unitColumns(units: FichaUnit[]): string[] {
    const order = ['Área', 'Unidades', 'Por andar', 'Banheiros', 'Lavabos', 'Vagas'];
    const norm = (t: string) => t.trim().toLowerCase();
    const extras: string[] = [];
    units.forEach((unit) =>
      unit.items.forEach((item) => {
        if (item.label && !order.some((o) => norm(o) === norm(item.label)) && !extras.includes(item.label)) {
          extras.push(item.label);
        }
      })
    );
    return [...order, ...extras];
  }

  unitValue(unit: FichaUnit, column: string): string {
    return unit.items.find((item) => item.label.trim().toLowerCase() === column.trim().toLowerCase())?.value ?? '-';
  }

  private buildUnits(category: any): FichaUnit[] {
    if (!category?.values) return [];

    return Object.entries(category.values as Record<string, string>)
      .filter(([, value]) => !this.isBlank(value))
      .map(([key, value]) => ({
        name: key.trim().replace(/^ap\b/i, 'Apartamento'),
        items: String(value)
          .split('|')
          .map((part) => part.trim())
          .filter((part) => part.length > 0)
          .map((part) => {
            const idx = part.indexOf(':');
            return idx === -1
              ? { label: '', value: part }
              : { label: part.slice(0, idx).trim(), value: part.slice(idx + 1).trim() };
          }),
      }));
  }

  private formatNumberValue(key: string, value: string): string {
    const trimmed = String(value).trim();
    const isArea = key.trim().toLowerCase().startsWith('área');
    const sufixoArea = isArea && !/m[²2]/i.test(trimmed) ? ' m²' : '';

    if (!/^-?[\d.,]+$/.test(trimmed)) return trimmed;

    const normalizado = /^-?\d{1,3}(\.\d{3})+(,\d+)?$/.test(trimmed)
      ? trimmed.replace(/\./g, '').replace(',', '.')
      : trimmed.replace(',', '.');

    const number = Number(normalizado);
    if (!isFinite(number)) return trimmed;

    const formatted = Number.isInteger(number)
      ? number.toLocaleString('pt-BR')
      : number.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    return `${formatted}${sufixoArea}`;
  }

  private buildTagSections(categories: any[]): FichaTagSection[] {
    return categories
      .filter((c) => this.isDictionaryCategory(c))
      .map((c) => ({
        title: this.getSectionTitle(c.name),
        groups: Object.entries(c.values as Record<string, string>)
          .filter(([, value]) => !this.isEmptyValue(value))
          .map(([key, value]) => ({
            title: key,
            tags: String(value)
              .split(', ')
              .map((tag) => tag.trim())
              .filter((tag) => tag.length > 0),
          })),
      }))
      .filter((section) => section.groups.length > 0);
  }

  private buildTextSections(categories: any[]): FichaTextSection[] {
    return categories
      .filter((c) => this.isDictionaryCategory(c))
      .map((c) => ({
        title: this.getSectionTitle(c.name),
        rows: Object.entries(c.values as Record<string, string>)
          .filter(([, value]) => !this.isEmptyValue(value))
          .map(([key, value]) => ({ label: this.getLabel(key), value: this.formatNumberValue(key, value) })),
      }))
      .filter((section) => section.rows.length > 0);
  }

  private buildTimelineSections(categories: any[]): FichaTimelineSection[] {
    return categories
      .filter((c) => Array.isArray(c?.values))
      .map((c) => ({
        title: this.getSectionTitle(c.name),
        items: (c.values as string[])
          .filter((entry) => !this.isBlank(entry))
          .map((entry) => this.splitTimelineEntry(entry)),
      }))
      .filter((section) => section.items.length > 0);
  }

  private splitTimelineEntry(entry: string): FichaTimelineItem {
    let text = entry.trim();
    let date: string | null = null;

    const trailing = text.match(/(\d{2}\/\d{2}\/\d{4})\.?\s*$/);
    const leading = text.match(/^(\d{2}\/\d{2}\/\d{4})\s*[-–:]?\s*/);

    if (trailing) {
      date = trailing[1];
      text = text.slice(0, trailing.index).trim();
    } else if (leading) {
      date = leading[1];
      text = text.slice(leading[0].length).trim();
    }

    return { date, title: null, text };
  }

  private buildUpdates(updates: any[] | null | undefined): FichaTimelineItem[] {
    if (!Array.isArray(updates)) return [];

    return updates.map((update) => ({
      date: update?.date ? new Date(update.date).toLocaleDateString('pt-BR') : null,
      title: this.clean(update?.title),
      text: this.clean(update?.detail) ?? (this.clean(update?.userName) ? `por ${update.userName.trim()}` : null),
    }));
  }

  private buildCompanies(companies: any[]): FichaCompany[] {
    return companies.map((company) => {
      const meta: string[] = [];

      if (!this.isBlank(company.companyRoleAlias)) meta.push(company.companyRoleAlias.trim());
      if (!this.isBlank(company.companyCNPJ)) meta.push(`CNPJ ${this.formatCnpj(company.companyCNPJ)}`);
      if (Number(company.companyTotalWorks) > 0) {
        meta.push(this.plural(Number(company.companyTotalWorks), 'obra ativa', 'obras ativas'));
      }

      const contacts: FichaContact[] = (company.contacts ?? []).map((contact: any) => ({
        name: contact.contactName?.trim() ?? '',
        role: this.isBlank(contact.contactRole) ? null : contact.contactRole.trim(),
        lines: [contact.contactEmail, contact.contactPhone1, contact.contactPhone2]
          .filter((line) => !this.isBlank(line))
          .map((line) => String(line).trim()),
      }));

      return {
        name: company.companyTradeName?.trim() ?? '',
        meta: meta.length ? meta.join(', ') : null,
        phones: [company.companyPhone1, company.companyPhone2]
          .filter((phone) => !this.isBlank(phone))
          .map((phone) => String(phone).trim()),
        contacts,
      };
    });
  }

  private isUnitsCategory(category: any): boolean {
    return (
      !!category?.values &&
      !Array.isArray(category.values) &&
      String(category.name ?? '').trim().toLowerCase().startsWith(UNITS_CATEGORY_PREFIX)
    );
  }

  private isDictionaryCategory(category: any): boolean {
    return (
      !!category?.values &&
      typeof category.values === 'object' &&
      !Array.isArray(category.values) &&
      !this.isUnitsCategory(category)
    );
  }

  private getLabel(key: string): string {
    return LABELS[key.trim().toLowerCase()] ?? key;
  }

  private getSectionTitle(name: string | null | undefined): string {
    const trimmed = (name ?? '').trim();
    return trimmed.toLowerCase() === MORE_DETAILS_NAME ? 'Mais detalhes' : trimmed;
  }

  private formatCnpj(cnpj: string): string {
    const digits = String(cnpj).replace(/\D/g, '');

    return digits.length === 14
      ? digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')
      : String(cnpj).trim();
  }

  private isBlank(value: any): boolean {
    const trimmed = value === null || value === undefined ? '' : String(value).trim();
    return trimmed === '' || trimmed === '-';
  }

  private isEmptyValue(value: any): boolean {
    return this.isBlank(value) || String(value).trim() === '0';
  }

  private clean(value: any): string | null {
    return this.isBlank(value) ? null : String(value).trim();
  }

  private plural(count: number, singular: string, pluralText: string): string {
    return `${count} ${count === 1 ? singular : pluralText}`;
  }
}